import React, { CSSProperties, useMemo } from "react";
import { getColorBaseTokenValue } from "../../design-tokens";
import { mergeClassNames } from "../../design-tokens/utils";
import { VagaTypeKey } from "../../services/stationState.types";
import { deriveVagaMeta } from "./floorLayout";
import styles from "./index.module.css";

/** Ícone Material Symbols por tipo de vaga. */
export const TYPE_ICON: Record<VagaTypeKey, string> = {
  Operacao: "elevator",
  Supermercado: "inventory_2",
  Extra: "block",
};

export interface VagaCardData {
  vagaKey: string;
  type: VagaTypeKey | null;
  /** "Livre" | "Ocupada" | outro. */
  state: string;
  /** Tempo decorrido já formatado. */
  elapsedText: string;
  /** Se a vaga já teve algum evento (elapsedSeconds !== null). */
  hasEvent: boolean;
}

export interface VagaCardProps {
  data: VagaCardData;
  typeLabel: string;
  stateLabel: string;
  large?: boolean;
  selected?: boolean;
  /** Modo compacto (retângulo sem os textos completos) — piso estreito. */
  compact?: boolean;
  /** Dentro do compacto: só a bolinha (telas pequenas). Caso contrário mostra a hora. */
  dotOnly?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
}

/**
 * Card de uma vaga no mapa de ocupação: tipo + código, estado (livre/ocupada) com
 * cor de semáforo e tempo no estado. Extra usa borda tracejada. Somente leitura.
 */
export const VagaCard: React.FC<VagaCardProps> = ({
  data,
  typeLabel,
  stateLabel,
  large = false,
  selected = false,
  compact = false,
  dotOnly = false,
  style,
  onClick,
}) => {
  const meta = useMemo(() => deriveVagaMeta(data.vagaKey), [data.vagaKey]);
  const effectiveType = data.type ?? meta.typeFromKey;
  const icon = effectiveType ? TYPE_ICON[effectiveType] : "help";
  // Borda por tipo (espessura/estilo), independente da cor (que reflete o estado):
  // Operação/elevador = contínua grossa; Supermercado = contínua fina; Extra = tracejada.
  const typeBorderClass =
    effectiveType === "Operacao"
      ? styles.vagaCardTypeOperacao
      : effectiveType === "Supermercado"
      ? styles.vagaCardTypeSupermercado
      : effectiveType === "Extra"
      ? styles.vagaCardTypeExtra
      : undefined;

  const cssVars = useMemo<CSSProperties>(() => {
    // Sem evento → neutro; Ocupada → danger (vermelho); Livre/demais → success (verde).
    let bg: string;
    let border: string;
    let text: string;
    let dot: string;
    let chipBg: string;

    if (!data.hasEvent) {
      bg = getColorBaseTokenValue("color.neutral.100");
      border = getColorBaseTokenValue("color.neutral.300");
      text = getColorBaseTokenValue("color.neutral.500");
      dot = getColorBaseTokenValue("color.neutral.300");
      chipBg = getColorBaseTokenValue("color.neutral.100");
    } else if (data.state === "Ocupada") {
      bg = getColorBaseTokenValue("color.feedback.danger.100");
      border = getColorBaseTokenValue("color.feedback.danger.300");
      text = getColorBaseTokenValue("color.feedback.danger.700");
      dot = getColorBaseTokenValue("color.feedback.danger.500");
      chipBg = getColorBaseTokenValue("color.feedback.danger.100");
    } else {
      bg = getColorBaseTokenValue("color.feedback.success.100");
      border = getColorBaseTokenValue("color.feedback.success.300");
      text = getColorBaseTokenValue("color.feedback.success.700");
      dot = getColorBaseTokenValue("color.feedback.success.500");
      chipBg = getColorBaseTokenValue("color.feedback.success.100");
    }

    return {
      "--vaga-bg": bg,
      "--vaga-border": border,
      "--vaga-text": text,
      "--vaga-dot": dot,
      "--vaga-chip-bg": chipBg,
      "--vaga-ring": dot,
      ...style,
    } as CSSProperties;
  }, [data.hasEvent, data.state, style]);

  // Compacto: retângulo arredondado sem os textos completos, mantendo POSIÇÃO real
  // (grid), cor por estado e borda por tipo (legenda). Tela média mostra a HORA em
  // tempo real; tela pequena mostra só a bolinha. O detalhe abre ao tocar (seleção).
  if (compact) {
    const showDot = dotOnly || !data.hasEvent;
    return (
      <button
        type="button"
        className={mergeClassNames(
          styles.vagaCard,
          styles.vagaCardCompact,
          typeBorderClass,
          selected && styles.vagaCardSelected
        )}
        style={cssVars}
        onClick={onClick}
        aria-pressed={selected}
        aria-label={`${meta.name} (${meta.code}) — ${stateLabel}`}
      >
        {showDot ? (
          <span className={styles.vagaDot} aria-hidden="true" />
        ) : (
          <span className={styles.vagaCompactTime}>{data.elapsedText}</span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={mergeClassNames(
        styles.vagaCard,
        large && styles.vagaCardLarge,
        typeBorderClass,
        selected && styles.vagaCardSelected
      )}
      style={cssVars}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={`${meta.name} (${meta.code}) — ${stateLabel}`}
    >
      <div className={styles.vagaHeader}>
        <span className={styles.vagaType}>
          <span className="material-symbols-outlined" aria-hidden="true">
            {icon}
          </span>
          {typeLabel}
        </span>
        <span className={styles.vagaCode}>{meta.code}</span>
      </div>

      <div className={styles.vagaStatus}>
        <span className={styles.vagaDot} aria-hidden="true" />
        {stateLabel}
      </div>

      <div className={styles.vagaTimeChip}>
        <span className="material-symbols-outlined" aria-hidden="true">
          schedule
        </span>
        {data.elapsedText}
      </div>
    </button>
  );
};

export default VagaCard;
